/**
 * The Speedy analytics events (F-129) and the row each becomes. A typed map only: the writing is
 * the existing `useTrackEntityAction` (or `api.logActivity` with keepalive while the page closes).
 * No email, transcript text or Stream URL is ever put in a row.
 */
import type { ActivityAction } from '@/hooks/use-entity-tracker';

export type SpeedyEntry = 'header_button' | 'feed_tile' | 'post_attachment' | 'learn_tab';
export type SpeedyExit = 'close' | 'back' | 'escape' | 'hidden';

export type SpeedyEvent =
  | {
    action: 'player_opened';
    entry: SpeedyEntry;
    speedyId: string | null;
    slot?: 0 | 1;
    tileIndex?: number;
    communityId?: string | null;
    channelId?: string | null;
  }
  | {
    action: 'player_session_ended';
    entry: SpeedyEntry;
    durationMs: number;
    itemsViewed: number;
    itemsCompleted: number;
    skips: number;
    exit: SpeedyExit;
  }
  | { action: 'feed_row_shown'; slot: 0 | 1; speedyIds: string[] }
  | { action: 'composer_list_opened' }
  | { action: 'composer_preview_played'; speedyId: string }
  | { action: 'composer_attached'; speedyId: string; previewed: boolean };

export interface SpeedyActivityRow {
  action: ActivityAction;
  entityType: 'speedy';
  entityId: string;
  /** The tracker skips a row with no name, so a Speedy row always carries one. */
  entityName: string;
  metadata: Record<string, unknown>;
}

/** The row for an event. Pure. */
export function speedyActivityRow(event: SpeedyEvent, sessionId: string, title?: string | null): SpeedyActivityRow {
  const { action, ...rest } = event;
  const id = 'speedyId' in event && event.speedyId ? event.speedyId : 'none';
  const metadata: Record<string, unknown> = { session_id: sessionId };
  for (const [k, v] of Object.entries(rest)) {
    if (k === 'speedyId' || v === undefined) continue;
    metadata[k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)] = v;
  }
  return {
    action,
    entityType: 'speedy',
    entityId: id,
    entityName: title?.trim() ? title.trim().slice(0, 120) : 'Speedy',
    metadata,
  };
}
